import type { HTMLAttributes, ReactNode } from 'react';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronIcon } from '../../icons';
import { classNames } from '../../utils/class-names';

import styles from './accordion.module.css';

export type AccordionProps = {
  isOpen?: boolean;
  isInitiallyOpen?: boolean;
  disabled?: boolean;
  buttonContent?: ReactNode | ((isOpen?: boolean) => ReactNode);
  buttonLabel?: string;
  buttonId?: string;
  contentId?: string;
  children?: ReactNode;
  compact?: boolean;
  buttonClassName?: string;
  onToggle?: (isOpen: boolean) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onToggle'>;

/**
 * @param {boolean} [isOpen] Whether the accordion is open (use it if you need to control the state externally)
 * @default undefined
 * @param {boolean} [isInitiallyOpen] Whether the accordion is open when the component is first rendered
 * @default false
 * @param {boolean} [disabled] Whether the accordion shouldn't be clickable
 * @default false
 * @param {ReactNode} [buttonContent] The content of the accordion's button
 * @default undefined
 * @param {string} [buttonLabel] The ARIA label of the accordion's button
 * @default undefined
 * @param {string} [buttonId] Accordion's button ID - used to connect itself the accordion's content panel using ARIA attributes
 * @default undefined
 * @param {string} [contentId] Accordion's content panel ID - used to connect itself to the accordion's button using ARIA attributes
 * @default undefined
 * @param {() => void} [onToggle] Callback to be called when the accordion is toggled
 * @default undefined
 * @param {boolean} [compact] Whether the accordion should be rendered in compact mode
 * @default false
 * @param {ReactNode} [children] The content of the accordion's panel
 * @default undefined
 */
export function Accordion({
  isOpen: externalIsOpen,
  isInitiallyOpen = false,
  disabled = false,
  buttonContent,
  buttonLabel,
  buttonId,
  contentId,
  className,
  buttonClassName,
  compact,
  children,
  onToggle,
  ...props
}: AccordionProps) {
  const { t } = useTranslation();
  const [internalIsOpen, setInternalIsOpen] = useState(isInitiallyOpen || externalIsOpen);

  const isControlledByProps = externalIsOpen !== undefined && onToggle !== undefined;
  const isOpen = isControlledByProps ? externalIsOpen : internalIsOpen;
  const defaultButtonLabel = isOpen ? t('accordion.button.closeLabel') : t('accordion.button.openLabel');

  const toggleIsOpen = () => {
    onToggle?.(!isOpen);
    setInternalIsOpen(!internalIsOpen);
  };

  return (
    <div
      className={classNames(styles.accordion, isOpen && styles.isOpen, disabled && styles.disabled, className)}
      {...props}
    >
      <button
        type="button"
        id={buttonId}
        data-testid={buttonId}
        aria-label={buttonLabel || defaultButtonLabel}
        aria-expanded={isOpen}
        aria-controls={contentId}
        title={buttonLabel || defaultButtonLabel}
        className={classNames(styles.tab, compact && styles.compact, buttonClassName)}
        disabled={disabled}
        onClick={toggleIsOpen}
      >
        {typeof buttonContent === 'function' ? buttonContent(isOpen) : buttonContent}
        <ChevronIcon className={styles.closeButton} />
      </button>

      <div id={contentId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>
        {children}
      </div>
    </div>
  );
}
